// Cadastro de conta financeira — criar e editar (Lucas, 22/09/2026: "precisamos
// conseguir cadastrar novos bancos no sistema" — a conta Safra de investimentos
// não tinha onde entrar; as 8 contas que existiam nasceram por migration).
//
// A régua de verdade mora no servidor (dre-engine/handlers/contaCadastro.js):
// banco de uma lista fechada (é comparado com o que o leitor de extrato
// produz), cartão fica no módulo Cartões, e tipo/banco não mudam depois de
// criados. Aqui a tela só mostra o que cabe a cada tipo e explica o efeito.
import { useEffect, useState } from 'react';
import { App as AntApp, Modal, Form, Input, Select, Segmented, Switch, Alert, Typography } from 'antd';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { BANCO_LABEL } from '../financeiro-extrato/types';

const { Text } = Typography;

export type ContaCadastro = {
  id: string; nome: string; apelido: string | null; banco: string; tipo: string;
  extrato_banco: string | null; extrato_conta_id: string | null; ativo: boolean;
  saldo_atual?: string | null; saldo_projetado?: string | null;
};

// Mesma lista do servidor (BANCOS_CADASTRO) — 'caixa' aqui é a Caixa Econômica;
// o "Caixa" de dinheiro em espécie é um TIPO, sem banco.
export const BANCOS = ['inter', 'itau', 'sicoob', 'safra', 'bradesco', 'bb', 'santander', 'caixa'];
export const rotuloBanco = (b: string) => (b === 'caixa' ? 'Caixa Econômica' : (BANCO_LABEL[b] || b));

// Rótulos curtos: com `block`, o Segmented corta o texto com reticências no
// celular (375px) — a explicação de cada tipo vai no `extra` do campo.
const TIPOS = [
  { value: 'corrente', label: 'Corrente' },
  { value: 'investimento', label: 'Investimento' },
  { value: 'caixa', label: 'Caixa' },
];
const TIPO_AJUDA: Record<string, string> = {
  corrente: 'Conta de movimento, que tem extrato no banco.',
  investimento: 'Aplicação (CDB, fundo…): o saldo é o montante que você informa.',
  caixa: 'Dinheiro em espécie, fora do banco.',
};
const TIPO_LABEL: Record<string, string> = {
  corrente: 'Conta corrente', investimento: 'Investimento', caixa: 'Caixa', cartao: 'Cartão',
};

export function ModalContaFinanceira({ conta, onFechar, onSalvou }: {
  conta: ContaCadastro | null;          // null = conta nova
  onFechar: () => void;
  onSalvou: (salva: ContaCadastro, nova: boolean) => void;
}) {
  const { message } = AntApp.useApp();
  const editando = !!conta;
  const [tipo, setTipo] = useState<string>(conta?.tipo || 'investimento');
  const [banco, setBanco] = useState<string | undefined>(conta?.banco);
  const [nome, setNome] = useState(conta?.nome || '');
  const [apelido, setApelido] = useState(conta?.apelido || '');
  const [contaExtrato, setContaExtrato] = useState(conta?.extrato_conta_id || '');
  const [ativo, setAtivo] = useState(conta ? conta.ativo !== false : true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Sugestão de nome enquanto a pessoa não escreveu um: "Banco Safra - Investimento".
  const [nomeMexido, setNomeMexido] = useState(editando);
  useEffect(() => {
    if (nomeMexido || editando) return;
    // "Caixa" já existe (a conta informal) e o servidor recusaria o nome
    // repetido — para caixa, a pessoa escreve (ex.: "Caixa da obra X").
    if (tipo === 'caixa') { setNome(''); return; }
    if (!banco) return;
    setNome(`Banco ${rotuloBanco(banco)} - ${tipo === 'investimento' ? 'Investimento' : 'Conta Corrente'}`);
  }, [tipo, banco, nomeMexido, editando]);

  const extratoTravado = editando && !!conta?.extrato_conta_id;
  const ehCartao = conta?.tipo === 'cartao';

  async function salvar() {
    setErro(null);
    if (!nome.trim()) { setErro('Dê um nome à conta.'); return; }
    if (!editando && tipo !== 'caixa' && !banco) { setErro('Escolha o banco.'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const corpo: any = editando
        ? {
            ...(ehCartao ? {} : { nome: nome.trim(), ativo }),
            apelido: apelido.trim(),
            ...(conta?.tipo === 'corrente' && !extratoTravado && contaExtrato.trim()
              ? { extrato_conta_id: contaExtrato.trim() } : {}),
          }
        : {
            nome: nome.trim(), apelido: apelido.trim(), tipo,
            banco: tipo === 'caixa' ? 'caixa' : banco,
            ...(tipo === 'corrente' && contaExtrato.trim() ? { extrato_conta_id: contaExtrato.trim() } : {}),
          };
      const r = await fetch(`${API_URL}/financeiro/contas${editando ? `/${conta!.id}` : ''}`, {
        method: editando ? 'PATCH' : 'POST',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify(corpo),
      });
      const d0 = await r.json().catch(() => ({}));
      const d = d0 && d0.body ? JSON.parse(d0.body) : d0;
      if (!r.ok || d?.erro || d?.error) {
        setErro(d?.erro || d?.error || 'Não consegui salvar. Tente de novo.');
        return;
      }
      message.success(editando ? 'Cadastro da conta atualizado.' : `Conta "${d.apelido || d.nome}" cadastrada.`);
      onSalvou(d, !editando);
    } catch (e: any) {
      setErro('Não consegui falar com o servidor: ' + (e?.message || e));
    } finally { setSalvando(false); }
  }

  return (
    <Modal
      open
      title={editando ? `Cadastro — ${conta!.apelido || conta!.nome}` : 'Nova conta'}
      onCancel={onFechar}
      onOk={salvar}
      okText={editando ? 'Salvar' : 'Cadastrar conta'}
      cancelText="Cancelar"
      confirmLoading={salvando}
      width="94vw"
      style={{ maxWidth: 640, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 170px)', overflowY: 'auto' } }}
      destroyOnHidden
    >
      <Form layout="vertical" size="middle" style={{ marginTop: 8 }}>
        <Form.Item label="Tipo" style={{ marginBottom: 12 }}
          extra={!editando ? <>{TIPO_AJUDA[tipo]} Cartão de crédito se cadastra em <b>Financeiro › Cartões</b> (botão "Novo cartão").</> : undefined}>
          {editando
            ? <Text strong>{TIPO_LABEL[conta!.tipo] || conta!.tipo}</Text>
            : <Segmented block value={tipo} onChange={(v) => setTipo(String(v))} options={TIPOS} />}
        </Form.Item>

        {(editando ? conta!.tipo !== 'caixa' : tipo !== 'caixa') && (
          <Form.Item label="Banco" style={{ marginBottom: 12 }}
            extra={!editando ? 'Não está na lista? O banco precisa ser incluído no sistema antes — o leitor de extrato tem que reconhecê-lo.' : undefined}>
            {editando
              ? <Text strong>{rotuloBanco(conta!.banco)}</Text>
              : <Select value={banco} onChange={setBanco} placeholder="Escolha o banco"
                  options={BANCOS.map(b => ({ value: b, label: rotuloBanco(b) }))} />}
          </Form.Item>
        )}

        <Form.Item label="Nome da conta" required style={{ marginBottom: 12 }}
          extra={ehCartao ? 'O nome da conta de cartão não muda — a fatura se liga a ela pelo nome.' : undefined}>
          <Input value={nome} disabled={ehCartao} maxLength={120}
            onChange={(e) => { setNome(e.target.value); setNomeMexido(true); }}
            placeholder={tipo === 'caixa' && !editando ? 'Ex.: "Caixa da obra X"' : 'Ex.: "Banco Safra - Investimento"'} />
        </Form.Item>

        <Form.Item label="Apelido (aparece nas listas)" style={{ marginBottom: 12 }}
          extra="Curto. Em branco, usa o nome.">
          <Input value={apelido} maxLength={60} onChange={(e) => setApelido(e.target.value)}
            placeholder='Ex.: "Safra Investimento"' />
        </Form.Item>

        {(editando ? conta!.tipo === 'corrente' : tipo === 'corrente') && (
          <Form.Item label="Número da conta no extrato (OFX)" style={{ marginBottom: 12 }}
            extra={extratoTravado
              ? 'Já ligado ao extrato — não muda por aqui (mudaria para onde a conciliação carimbou as parcelas).'
              : 'Como aparece no arquivo OFX do banco (ex.: 58856-3). Pode deixar em branco e ligar depois.'}>
            <Input value={contaExtrato} disabled={extratoTravado} maxLength={40}
              onChange={(e) => setContaExtrato(e.target.value)} placeholder="Número da conta" />
          </Form.Item>
        )}

        {editando && !ehCartao && (
          <Form.Item label="Conta ativa" style={{ marginBottom: 12 }}
            extra="Desativada some das listas e da posição da empresa. Só dá para desativar com saldo zero.">
            <Switch checked={ativo} onChange={setAtivo} />
          </Form.Item>
        )}

        {!editando && tipo === 'investimento' && (
          <Alert type="info" showIcon style={{ marginBottom: 8 }} message={
            <span style={{ fontSize: 12.5 }}>
              O saldo do investimento é o <b>montante que você informa</b> (botão "Informar montante",
              com o valor do app do banco). Dinheiro que entra ou sai desta conta: lance em{' '}
              <b>Extrato bancário › Transferências</b>.
            </span>} />
        )}
        {!editando && tipo === 'corrente' && !contaExtrato.trim() && (
          <Alert type="warning" showIcon style={{ marginBottom: 8 }} message={
            <span style={{ fontSize: 12.5 }}>
              Sem o número do extrato, o saldo desta conta fica parado no saldo inicial até alguém ligá-lo.
            </span>} />
        )}
        {erro && <Alert type="error" showIcon message={erro} />}
      </Form>
    </Modal>
  );
}
